import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import { CCallout, CStepper } from "@coreui/react-pro";
import config from "../../config/config";
import EmployerPageFrame from "../../components/employer/EmployerPageFrame";
import useEmployerOsWorkspace from "../../hooks/useEmployerOsWorkspace";
import { buildEmployerTrustSummary } from "../../services/employer/employerTrustDomain";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { Badge } from "../../components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { buildAuthHeaders } from "../../utils/authSession";

const arbeitsformen = ["Vollzeit", "Teilzeit", "Minijob", "Schicht", "HomeOffice", "Hybrid", "Befristet", "Unbefristet"];
const wohnungsoptionen = [
  { value: "hilfe_bei_suche", label: "Hilfe bei der Unterkunftssuche" },
  { value: "unterkunft_wird_gestellt", label: "Unterkunft wird gestellt" },
];
const MIN_WILLKOMMENSBONUS = 1500;

function hasValue(value) {
  return String(value || "").trim().length > 0;
}

function getStepValidationError(form, targetStep) {
  const normalizedStep = Math.min(4, Math.max(1, Number(targetStep) || 1));

  if (normalizedStep >= 2) {
    if (!hasValue(form.beruf_text)) return "Schritt 1: Bitte zuerst einen Beruf festlegen.";
    if (!hasValue(form.titel)) return "Schritt 1: Der Anzeigentitel fehlt noch.";
    if (!hasValue(form.arbeitsform)) return "Schritt 1: Bitte eine Arbeitsform waehlen.";
  }

  if (normalizedStep >= 3) {
    if (!hasValue(form.beschreibung)) return "Schritt 2: Bitte zuerst die Beschreibung ausfuellen.";
  }

  if (normalizedStep >= 4) {
    if (Number(form.onboarding_bonus) < MIN_WILLKOMMENSBONUS) return "Schritt 3: Der Willkommensbonus muss mindestens 1.500 EUR betragen.";
    if (Number(form.wohnung) !== 1) return "Schritt 3: Wohnungsunterstuetzung ist verpflichtend.";
    if (!hasValue(form.wohnungsunterstuetzung_art)) return "Schritt 3: Bitte die Art der Wohnunterstuetzung festlegen.";
  }

  return null;
}

function authHeaders() {
  return buildAuthHeaders();
}

function normalizeBonusInput(value) {
  if (value === "") return "";
  const numericValue = Number(value);
  if (!Number.isFinite(numericValue)) return MIN_WILLKOMMENSBONUS;
  return Math.max(MIN_WILLKOMMENSBONUS, numericValue);
}

function useQuery() {
  return new URLSearchParams(useLocation().search);
}

const emptyForm = {
  beruf_id: "",
  beruf_text: "",
  titel: "",
  standort_land: "DE",
  standort_ort: "",
  arbeitsform: "Vollzeit",
  beschreibung: "",
  aufgaben: "",
  anforderungen: "",
  benefits: "",
  sprachen: "",
  gehalt_von: "",
  gehalt_bis: "",
  gehalt_text: "",
  onboarding_bonus: 1500,
  wohnung: 1,
  wohnungsunterstuetzung_art: "hilfe_bei_suche",
  laufzeit_monate: 12,
};

const StbAdvCreate = () => {
  const navigate = useNavigate();
  const workspace = useEmployerOsWorkspace();
  const query = useQuery();
  const editId = query.get("id");
  const initialStep = Number(query.get("step") || 1);

  const [step, setStep] = useState([1, 2, 3, 4].includes(initialStep) ? initialStep : 1);
  const [form, setForm] = useState(emptyForm);
  const [berufQuery, setBerufQuery] = useState("");
  const [berufe, setBerufe] = useState([]);
  const [loadingBerufe, setLoadingBerufe] = useState(false);
  const [berufeError, setBerufeError] = useState("");
  const [saving, setSaving] = useState(false);
  const [publishLoading, setPublishLoading] = useState(false);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const [createdId, setCreatedId] = useState(editId ? Number(editId) : null);
  const [suggestions, setSuggestions] = useState({});
  const [gptLoadingField, setGptLoadingField] = useState("");
  const trustSummary = useMemo(() => buildEmployerTrustSummary(workspace.profile || {}), [workspace.profile]);

  useEffect(() => {
    document.title = "Experts4Europe | Neue Anzeige";
  }, []);

  useEffect(() => {
    if (!editId) return;
    let mounted = true;
    axios
      .get(`${config.API_BASE_URL}/api/stb/employer/ads/${editId}`, { headers: authHeaders() })
      .then((res) => {
        if (!mounted) return;
        const ad = res.data || {};
        setForm({
          ...emptyForm,
          ...ad,
          onboarding_bonus: ad.onboarding_bonus ?? 1500,
          wohnung: Number(ad.wohnung ?? 1),
          wohnungsunterstuetzung_art: ad.wohnungsunterstuetzung_art || "hilfe_bei_suche",
          laufzeit_monate: ad.laufzeit_monate ?? 12,
          sprachen: Array.isArray(ad.sprachen) ? ad.sprachen.join(", ") : "",
        });
        setCreatedId(Number(editId));
      })
      .catch(() => setError("Anzeige konnte nicht geladen werden."))
      .finally(() => {
        mounted = false;
      });
  }, [editId]);

  useEffect(() => {
    if (!berufQuery || berufQuery.trim().length < 2) {
      setBerufe([]);
      return;
    }
    const t = setTimeout(async () => {
      try {
        setBerufeError("");
        setLoadingBerufe(true);
        const res = await axios.get(`${config.API_BASE_URL}/api/stb/berufe`, {
          params: { q: berufQuery.trim(), limit: 20 },
          headers: authHeaders(),
        });
        setBerufe(res.data || []);
      } catch (err) {
        const msg = err.response?.data?.message || err.message || "Berufssuche fehlgeschlagen.";
        setBerufeError(msg);
        setBerufe([]);
      } finally {
        setLoadingBerufe(false);
      }
    }, 350);
    return () => clearTimeout(t);
  }, [berufQuery]);

  const contextPayload = useMemo(
    () => ({
      beruf_text: form.beruf_text,
      titel: form.titel,
      arbeitsform: form.arbeitsform,
      standort_land: form.standort_land,
      standort_ort: form.standort_ort,
      onboarding_bonus: form.onboarding_bonus,
      wohnungsunterstuetzung_art: form.wohnungsunterstuetzung_art,
      tonality: "professionell",
    }),
    [form.beruf_text, form.titel, form.arbeitsform, form.standort_land, form.standort_ort, form.onboarding_bonus, form.wohnungsunterstuetzung_art]
  );

  const updateForm = (key, value) => setForm((prev) => ({ ...prev, [key]: value }));
  const clampWillkommensbonus = () => {
    const numericValue = Number(form.onboarding_bonus || 0);
    if (!Number.isFinite(numericValue) || numericValue < MIN_WILLKOMMENSBONUS) {
      updateForm("onboarding_bonus", MIN_WILLKOMMENSBONUS);
    }
  };

  const validate = () => getStepValidationError(form, 4);

  const goToStep = (targetStep) => {
    const normalizedStep = Math.min(4, Math.max(1, Number(targetStep) || 1));
    const validationError = getStepValidationError(form, normalizedStep);
    if (validationError) {
      setError(validationError);
      return;
    }
    setError("");
    setStep(normalizedStep);
  };

  const stepNavigationMeta = useMemo(() => {
    const nextStep = step < 4 ? step + 1 : null;
    const nextStepLabelMap = {
      2: "Weiter zu Inhalt",
      3: "Weiter zu Konditionen",
      4: "Weiter zur Preview",
    };
    const helperMap = {
      1: "Basisdaten festlegen und dann in den Inhalt wechseln.",
      2: "Beschreibung vervollstaendigen und danach zu den Konditionen gehen.",
      3: "Ankunftshilfe und Bonus pruefen und dann in die Preview wechseln.",
      4: "In der Preview kannst du den Entwurf speichern oder veroeffentlichen.",
    };
    return {
      nextStep,
      nextStepLabel: nextStep ? nextStepLabelMap[nextStep] : "",
      nextStepError: nextStep ? getStepValidationError(form, nextStep) : null,
      helper: helperMap[step] || "",
    };
  }, [form, step]);

  const payload = () => ({
    ...form,
    onboarding_bonus: Number(form.onboarding_bonus || 0),
    wohnung: Number(form.wohnung || 0),
    laufzeit_monate: Number(form.laufzeit_monate || 0),
    gehalt_von: form.gehalt_von ? Number(form.gehalt_von) : null,
    gehalt_bis: form.gehalt_bis ? Number(form.gehalt_bis) : null,
    sprachen: form.sprachen
      ? form.sprachen.split(",").map((v) => v.trim()).filter(Boolean)
      : [],
  });

  const saveDraft = async () => {
    setError("");
    setMessage("");
      const v = validate();
      if (v) {
        setError(v);
        return null;
      }
    try {
      setSaving(true);
      if (createdId) {
        await axios.put(`${config.API_BASE_URL}/api/stb/employer/ads/${createdId}`, payload(), { headers: authHeaders() });
        setMessage("Draft gespeichert.");
        return createdId;
      } else {
        const res = await axios.post(`${config.API_BASE_URL}/api/stb/employer/ads`, payload(), { headers: authHeaders() });
        const newId = res.data?.ad?.id || null;
        setCreatedId(newId);
        setMessage("Draft gespeichert.");
        return newId;
      }
    } catch (e) {
      const detail = e.response?.data?.detail ? ` ${e.response.data.detail}` : "";
      setError((e.response?.data?.message || "Speichern fehlgeschlagen.") + detail);
      return null;
    } finally {
      setSaving(false);
    }
  };

  const publish = async () => {
    setError("");
    setMessage("");
      if (!trustSummary.canPublishLive) {
        return setError(trustSummary.publishBlockers[0] || "Employer Trust Gate blockiert die Live-Schaltung.");
      }
      const v = validate();
      if (v) return setError(v);
    try {
      let id = createdId || Number(editId);
      if (!id) {
        id = await saveDraft();
      }
      if (!id) return;
      setPublishLoading(true);
      await axios.post(`${config.API_BASE_URL}/api/stb/employer/ads/${id}/publish`, {}, { headers: authHeaders() });
      setCreatedId(id);
      setMessage("Anzeige wurde veroeffentlicht.");
    } catch (e) {
      setError(e.response?.data?.message || "Veroeffentlichen fehlgeschlagen.");
    } finally {
      setPublishLoading(false);
    }
  };

  const suggestForField = async (field) => {
    try {
      setGptLoadingField(field);
      const res = await axios.post(
        `${config.API_BASE_URL}/api/stb/ads/gpt/suggest`,
        { field, input: form[field] || "", context: contextPayload },
        { headers: authHeaders() }
      );
      setSuggestions((prev) => ({ ...prev, [field]: res.data }));
    } catch (e) {
      const msg = e.response?.data?.message || e.message || "GPT Vorschlag fehlgeschlagen.";
      setError(msg);
    } finally {
      setGptLoadingField("");
    }
  };

  const suggestionCard = (field) => {
    const box = suggestions[field];
    if (!box) return null;
    return (
      <div className="mt-2 rounded-xl border border-sky-200 bg-sky-50 p-3 text-sm text-slate-700">
        <p className="font-semibold text-slate-900">Vorschlag</p>
        <p className="mt-1 whitespace-pre-wrap">{box.suggestion}</p>
        <div className="mt-2 flex flex-wrap gap-2">
          <Button size="sm" className="dashboard-action-button" onClick={() => updateForm(field, box.suggestion || "")}>
            Uebernehmen
          </Button>
          {(box.alternatives || []).map((alt, idx) => (
            <Button key={`${field}_${idx}`} size="sm" variant="outline" className="dashboard-inline-button" onClick={() => updateForm(field, alt)}>
              Alternative {idx + 1}
            </Button>
          ))}
        </div>
      </div>
    );
  };

  return (
    <EmployerPageFrame
      title={editId ? "Anzeige bearbeiten" : "Anzeigen-Wizard"}
      subtitle="Der Anzeigenbereich arbeitet jetzt mit genau einem Wizard fuer Erstellen, Bearbeiten und Preview. Es gibt keine zweite Editorfamilie mehr."
      badge="Employer Job Wizard"
      shellProps={{
        shellLabel: "Employer Job OS",
        shellDescription: "Ein Wizard fuer Anzeige, Preview und Veroeffentlichung statt verteilter Legacy-Editoren.",
        topContext: editId ? "Anzeige bearbeiten" : "Anzeigen-Wizard",
      }}
      actions={
        <>
          <Button variant="outline" className="dashboard-action-button dashboard-action-button-secondary" onClick={() => navigate("/dashboard/employer/jobs")}>
            Zu Anzeigen
          </Button>
          <Button className="dashboard-action-button dashboard-action-button-secondary" onClick={saveDraft} disabled={saving}>
            {saving ? "Speichert..." : "Entwurf speichern"}
          </Button>
          <Button className="dashboard-action-button" onClick={publish} disabled={publishLoading || !trustSummary.canPublishLive}>
            {publishLoading ? "Veroeffentlicht..." : "Veroeffentlichen"}
          </Button>
        </>
      }
      contentClassName="space-y-4"
    >
      {message ? <p className="rounded-lg border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-700">{message}</p> : null}
      {error ? <p className="rounded-lg border border-rose-200 bg-rose-50 p-3 text-sm text-rose-700">{error}</p> : null}

      <CCallout color="info">
        <strong>Wizard statt Editorfamilie.</strong>
        <div>Basisdaten, Inhalt, Konditionen und Preview laufen in einer CStepper-gefuehrten Surface. Alte `edit`-, `preview`- und Nebenpfade werden hier zusammengezogen.</div>
      </CCallout>

      <CCallout color={trustSummary.canPublishLive ? "success" : "warning"}>
        <strong>{trustSummary.canPublishLive ? "Trust Gate ist publishing-ready." : "Trust Gate blockiert die Live-Schaltung."}</strong>
        <div>{trustSummary.canPublishLive ? "Der Employer ist als Direktarbeitgeber verifiziert und die erste Anzeige ist freigegeben." : trustSummary.publishBlockers[0] || trustSummary.nextTrustAction}</div>
      </CCallout>

      <Card className="dashboard-card-shell">
        <CardHeader>
          <CardTitle className="dashboard-card-title">Schritt {step} / 4</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <CStepper
            activeStepNumber={step}
            layout="horizontal"
            onStepChange={goToStep}
            steps={[
              { label: "Basis" },
              { label: "Inhalt" },
              { label: "Konditionen" },
              { label: "Preview" },
            ]}
          />
        </CardContent>
      </Card>

      {step === 1 ? (
        <Card className="dashboard-card-shell">
          <CardHeader><CardTitle className="dashboard-card-title">Basisdaten</CardTitle></CardHeader>
          <CardContent className="space-y-4">
            <div>
              <label className="mb-1 block text-sm text-slate-600">Beruf suchen</label>
              <Input value={berufQuery} onChange={(e) => setBerufQuery(e.target.value)} placeholder="z.B. Pflegefachkraft" className="dashboard-field" />
              {loadingBerufe ? <p className="mt-2 text-xs text-slate-500">Suche...</p> : null}
              {berufeError ? <p className="mt-2 text-xs text-rose-600">{berufeError}</p> : null}
              {berufe.length > 0 ? (
                <div className="mt-2 max-h-56 overflow-auto rounded-xl border border-slate-200 bg-white">
                  {berufe.map((b) => (
                    <button key={b.id} type="button" className="w-full border-b border-slate-100 px-3 py-2 text-left text-sm text-slate-700 hover:bg-slate-50" onClick={() => {
                      updateForm("beruf_id", b.id);
                      updateForm("beruf_text", b.beruf);
                      setBerufQuery(b.beruf);
                      setBerufe([]);
                    }}>
                      {b.beruf}
                    </button>
                  ))}
                </div>
              ) : null}
            </div>
            <Input value={form.beruf_text} onChange={(e) => updateForm("beruf_text", e.target.value)} placeholder="Beruf (aktueller Stand)" className="dashboard-field" />
            <Input value={form.titel} onChange={(e) => updateForm("titel", e.target.value)} placeholder="Anzeigentitel" className="dashboard-field" />
            <div className="grid gap-3 md:grid-cols-3">
              <Select value={form.arbeitsform} onValueChange={(v) => updateForm("arbeitsform", v)}>
                <SelectTrigger className="dashboard-field"><SelectValue placeholder="Arbeitsform" /></SelectTrigger>
                <SelectContent>{arbeitsformen.map((f) => <SelectItem key={f} value={f}>{f}</SelectItem>)}</SelectContent>
              </Select>
              <Input value={form.standort_land} onChange={(e) => updateForm("standort_land", e.target.value.toUpperCase())} placeholder="Land (DE)" className="dashboard-field" />
              <Input value={form.standort_ort} onChange={(e) => updateForm("standort_ort", e.target.value)} placeholder="Ort" className="dashboard-field" />
            </div>
          </CardContent>
        </Card>
      ) : null}

      {step === 2 ? (
        <Card className="dashboard-card-shell">
          <CardHeader><CardTitle className="dashboard-card-title">Inhalt + GPT-Unterstuetzung</CardTitle></CardHeader>
          <CardContent className="space-y-4">
            {[
              { key: "beschreibung", label: "Beschreibung" },
              { key: "aufgaben", label: "Aufgaben" },
              { key: "anforderungen", label: "Anforderungen" },
              { key: "gehalt_text", label: "Gehaltstext" },
            ].map((item) => (
              <div key={item.key}>
                <div className="mb-1 flex items-center justify-between">
                  <label className="text-sm text-slate-600">{item.label}</label>
                  <Button size="sm" variant="outline" className="dashboard-inline-button" onClick={() => suggestForField(item.key)}>
                    {gptLoadingField === item.key ? "..." : "✨ Vorschlag"}
                  </Button>
                </div>
                <Textarea rows={5} value={form[item.key] || ""} onChange={(e) => updateForm(item.key, e.target.value)} className="dashboard-field dashboard-textarea" />
                {suggestionCard(item.key)}
              </div>
            ))}
            <Input value={form.sprachen} onChange={(e) => updateForm("sprachen", e.target.value)} placeholder="Sprachen, kommasepariert (z.B. Deutsch B2, Englisch B1)" className="dashboard-field" />
            <div>
              <div className="mb-1 flex items-center justify-between">
                <label className="text-sm text-slate-600">Leistungen</label>
                <Button size="sm" variant="outline" className="dashboard-inline-button" onClick={() => suggestForField("benefits")}>
                  {gptLoadingField === "benefits" ? "..." : "✨ Vorschlag"}
                </Button>
              </div>
              <Textarea rows={4} value={form.benefits || ""} onChange={(e) => updateForm("benefits", e.target.value)} className="dashboard-field dashboard-textarea" />
              {suggestionCard("benefits")}
            </div>
          </CardContent>
        </Card>
      ) : null}

      {step === 3 ? (
        <Card className="dashboard-card-shell">
          <CardHeader><CardTitle className="dashboard-card-title">Konditionen</CardTitle></CardHeader>
          <CardContent className="grid gap-3 md:grid-cols-3">
            <Input type="number" value={form.gehalt_von} onChange={(e) => updateForm("gehalt_von", e.target.value)} placeholder="Gehalt von" className="dashboard-field" />
            <Input type="number" value={form.gehalt_bis} onChange={(e) => updateForm("gehalt_bis", e.target.value)} placeholder="Gehalt bis" className="dashboard-field" />
            <Input value={form.gehalt_text} onChange={(e) => updateForm("gehalt_text", e.target.value)} placeholder="Gehaltstext (optional)" className="dashboard-field" />
            <div className="space-y-2">
              <Input
                type="number"
                min="1500"
                step="100"
                value={form.onboarding_bonus}
                onChange={(e) => updateForm("onboarding_bonus", normalizeBonusInput(e.target.value))}
                onBlur={clampWillkommensbonus}
                placeholder="Willkommensbonus ab 1500"
                className={`dashboard-field ${Number(form.onboarding_bonus || 0) < MIN_WILLKOMMENSBONUS ? "border-rose-300 bg-rose-50" : ""}`}
              />
              <p className={`text-xs ${Number(form.onboarding_bonus || 0) < MIN_WILLKOMMENSBONUS ? "text-rose-700" : "text-slate-500"}`}>
                Mindestwert: 1.500 EUR. Niedrigere Eingaben werden automatisch auf 1.500 EUR angehoben.
              </p>
            </div>
            <Select value={form.wohnungsunterstuetzung_art} onValueChange={(v) => updateForm("wohnungsunterstuetzung_art", v)}>
              <SelectTrigger className="dashboard-field"><SelectValue placeholder="Wohnunterstuetzung" /></SelectTrigger>
              <SelectContent>{wohnungsoptionen.map((option) => <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>)}</SelectContent>
            </Select>
            <Select value={String(form.wohnung)} onValueChange={(v) => updateForm("wohnung", Number(v))}>
              <SelectTrigger className="dashboard-field"><SelectValue placeholder="Wohnunterstuetzung Pflicht" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="1">Wohnungsunterstuetzung: Ja (Pflicht)</SelectItem>
                <SelectItem value="0">Nein (nicht erlaubt)</SelectItem>
              </SelectContent>
            </Select>
            <Input type="number" value={form.laufzeit_monate} onChange={(e) => updateForm("laufzeit_monate", e.target.value)} placeholder="Laufzeit Monate" className="dashboard-field" />
            <div className="md:col-span-3 rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900">
              <p className="font-semibold">Verbindliche Ankunftshilfe fuer internationale Fachkraefte</p>
              <p className="mt-1">
                Jede Anzeige muss Hilfe bei der Unterkunftssuche oder eine gestellte Unterkunft enthalten sowie einen
                Willkommensbonus von mindestens 1.500 EUR.
              </p>
            </div>
          </CardContent>
        </Card>
      ) : null}

      {step === 4 ? (
        <Card className="dashboard-card-shell">
          <CardHeader><CardTitle className="dashboard-card-title">Vorschau</CardTitle></CardHeader>
          <CardContent className="space-y-3 text-sm text-slate-700">
            <div className="flex flex-wrap gap-2">
              <Badge variant="outline" className="border-sky-200 bg-sky-50 text-sky-700">{form.beruf_text || "-"}</Badge>
              <Badge variant="outline" className="border-slate-200 bg-slate-50 text-slate-700">{form.arbeitsform || "-"}</Badge>
              <Badge variant="outline" className="border-emerald-200 bg-emerald-50 text-emerald-700">Willkommensbonus {Number(form.onboarding_bonus || 0)} EUR</Badge>
              <Badge variant="outline" className={Number(form.wohnung) === 1 ? "border-emerald-200 bg-emerald-50 text-emerald-700" : "border-rose-200 bg-rose-50 text-rose-700"}>
                {form.wohnungsunterstuetzung_art === "unterkunft_wird_gestellt" ? "Unterkunft wird gestellt" : "Hilfe bei Unterkunftssuche"}
              </Badge>
            </div>
            <h2 className="text-2xl font-semibold text-slate-900">{form.titel || "(Titel fehlt)"}</h2>
            <p><strong>Standort:</strong> {[form.standort_land, form.standort_ort].filter(Boolean).join(" / ") || "-"}</p>
            <p className="whitespace-pre-wrap">{form.beschreibung || "-"}</p>
            <p className="whitespace-pre-wrap"><strong>Aufgaben:</strong>{"\n"}{form.aufgaben || "-"}</p>
            <p className="whitespace-pre-wrap"><strong>Anforderungen:</strong>{"\n"}{form.anforderungen || "-"}</p>
            <p className="whitespace-pre-wrap"><strong>Leistungen:</strong>{"\n"}{form.benefits || "-"}</p>
            <p className="whitespace-pre-wrap"><strong>Ankunftshilfe:</strong>{"\n"}{form.wohnungsunterstuetzung_art === "unterkunft_wird_gestellt" ? "Unterkunft wird gestellt." : "Hilfe bei der Suche einer geeigneten Unterkunft."}</p>
            <p><strong>Willkommensbonus:</strong> mindestens {Number(form.onboarding_bonus || 0)} EUR</p>
            <div className="flex flex-wrap gap-2">
              <Button className="dashboard-action-button" onClick={saveDraft}>Entwurf speichern</Button>
              <Button className="dashboard-action-button-success" onClick={publish} disabled={!trustSummary.canPublishLive}>Veroeffentlichen</Button>
              {createdId ? (
                <Button variant="outline" className="dashboard-inline-button" onClick={() => navigate(`/dashboard/employer/candidates?adId=${createdId}`)}>
                  Folgeflaeche oeffnen
                </Button>
              ) : null}
            </div>
          </CardContent>
        </Card>
      ) : null}

      <Card className="dashboard-card-shell">
        <CardContent className="flex flex-col gap-4 py-6 lg:flex-row lg:items-center lg:justify-between">
          <div className="space-y-1">
            <p className="text-sm font-semibold text-slate-900">Naechster Schritt</p>
            <p className="text-sm text-slate-600">{stepNavigationMeta.helper}</p>
            {stepNavigationMeta.nextStepError ? (
              <p className="text-sm text-rose-700">{stepNavigationMeta.nextStepError}</p>
            ) : stepNavigationMeta.nextStep ? (
              <p className="text-sm text-emerald-700">{stepNavigationMeta.nextStepLabel} ist freigegeben.</p>
            ) : (
              <p className="text-sm text-slate-600">Die Anzeige ist jetzt in der Preview und kann von dort gespeichert oder veroeffentlicht werden.</p>
            )}
          </div>
          <div className="flex flex-wrap gap-2">
            {step > 1 ? (
              <Button
                type="button"
                variant="outline"
                className="dashboard-action-button dashboard-action-button-secondary"
                onClick={() => {
                  setError("");
                  setStep(Math.max(1, step - 1));
                }}
              >
                Zurueck
              </Button>
            ) : null}
            {stepNavigationMeta.nextStep ? (
              <Button
                type="button"
                className="dashboard-action-button"
                onClick={() => goToStep(stepNavigationMeta.nextStep)}
              >
                {stepNavigationMeta.nextStepLabel}
              </Button>
            ) : null}
          </div>
        </CardContent>
      </Card>
    </EmployerPageFrame>
  );
};

export default StbAdvCreate;
